import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import { graphql, type DocumentType } from '@/gql';
import { GroupOrServiceAvatar } from '@/components/Avatar/GroupOrServiceAvatar';

import { AcceptPendingInviteButton } from './AcceptPendingInviteButton';
import { DeclinePendingInviteButton } from './DeclinePendingInviteButton';
import { PendingInvitesListSkeleton } from './PendingInvitesListSkeleton';

const PENDING_INVITES_LIST_GROUPS_FRAGMENT = graphql(`
  fragment pendingInvitesListGroupsFields on Group {
    ...acceptPendingInviteButtonGroupFields
    ...declinePendingInviteButtonGroupFields
    _id
    name
    referenceName
    iconUrl
  }
`);

interface Props {
  groups:
    | Array<DocumentType<typeof PENDING_INVITES_LIST_GROUPS_FRAGMENT>>
    | null
    | undefined;
  loading: boolean;
}

export const PendingInvitesList = ({ groups, loading }: Props) => {
  if (loading && !groups?.length) {
    return <PendingInvitesListSkeleton />;
  }

  if (groups?.length === 0) {
    return (
      <Typography color="textSecondary" sx={{ px: 2, py: 2 }}>
        You have no pending invitations
      </Typography>
    );
  }

  return (
    <List disablePadding>
      {groups?.map((group, index) => (
        <ListItem
          key={group._id}
          divider={index < groups.length - 1}
          sx={{
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            rowGap: 1,
          }}
        >
          <ListItemAvatar>
            <GroupOrServiceAvatar
              iconUrl={group.iconUrl}
              groupOrServiceName={group.name}
              size={40}
            />
          </ListItemAvatar>
          <ListItemText primary={group.name} secondary={group.referenceName} />
          <Stack
            direction="row"
            spacing={1}
            sx={{
              ml: { sm: 2 },
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'flex-end', sm: 'flex-start' },
            }}
          >
            <AcceptPendingInviteButton group={group} />
            <DeclinePendingInviteButton group={group} />
          </Stack>
        </ListItem>
      ))}
    </List>
  );
};
